import { api } from './api';

export type RowKind = 'design_change' | 'failure_mode' | 'countermeasure';
export type ExtractionMode = 'focused' | 'exhaustive';
export interface ChangeMatch {
  key: string; status: 'new' | 'duplicate' | 'possible_match'; existing_ids: string[];
}
export interface ChangeResolution {
  key: string; action: 'add' | 'skip' | 'update'; target_id?: string;
}
export interface ExtractionResult {
  mode: ExtractionMode; presentation_limit: number | null; presented_count: number;
  input_version: string; status: 'completed';
  source_characters: { old_text: number; new_text: number };
}
export interface CausalClaim {
  claim: string; evidence_level: 'direct' | 'supported' | 'assumption'; evidence?: string | null;
}
export type RowFields = Record<string, string | number | null | CausalClaim[]>;
export interface MainlineEvidence {
  kind: 'input_fact' | 'internal_document' | 'public_information' | 'ai_assumption';
  title: string; excerpt: string; document_id?: string; url?: string;
  retrieved_on?: string; applicability: string;
}
export interface MainlineRowInput {
  kind: RowKind; key?: string; parent_id?: string | null; parent_key?: string | null;
  fields: RowFields; unknown_conditions: string[]; evidence: MainlineEvidence[];
}
export interface MainlineMetadata {
  origin?: string; analysis_pending?: boolean; needs_recheck?: boolean; recheck_reason?: string;
  unknown_conditions?: string[]; evidence?: MainlineEvidence[]; order?: number;
  severity_confirmed?: boolean; change_kind?: string; execution_status?: string;
  verification_status?: string; owner?: string; due_date?: string; last_edit_origin?: 'ai' | 'manual';
  /** The AI value a person replaced, kept once per field (#1613). */
  ai_original?: Record<string, unknown>;
  ai_written_fields?: string[]; corrected_at_apply_fields?: string[];
}
export interface MainlineRow {
  id: string; session_id: string; review_status: string;
  design_change_id?: string; failure_mode_id?: string;
  mainline_metadata?: MainlineMetadata;
  [field: string]: unknown;
}
export interface ReferenceScope {
  document_id: string; title: string; version_id: string; total_characters: number;
  referenced_characters: number; range: string; truncated: boolean;
}
export interface MainlineInputs {
  pattern: 'old_new_documents' | 'old_requirements' | 'change_report' | 'manual';
  extraction_mode?: ExtractionMode | null;
  old_text: string; new_text: string; report_text: string;
  old_document_id?: string | null; new_document_id?: string | null; report_document_id?: string | null;
  selected_document_ids: string[]; material: string; dimensions: string;
  operating_conditions: string; constraints: string; evidence: MainlineEvidence[];
}
export interface Proposal {
  mainline_proposal: true; operation: string; kind?: RowKind; before?: MainlineRow;
  after?: MainlineRowInput; rows?: MainlineRowInput[]; reason: string;
  reference_scope: ReferenceScope[]; input_version: string; run_id: string;
  reference_note?: string;
  change_matches?: ChangeMatch[]; extraction?: ExtractionResult;
  /** Server prediction for a failure-mode revision: fields whose apply changes content, and whether conditions or evidence do. */
  score_reset?: { fields: string[]; conditions_or_evidence: boolean };
}
export interface MainlineArtifact {
  id: string; status: string; title: string;
  payload: Partial<MainlineInputs & Proposal> & {
    mainline_inputs?: boolean; reference_scope?: ReferenceScope[];
  };
}
export interface MainlineState {
  version: string; session: { id: string; item_name: string; workflow_metadata?: Record<string, unknown> };
  extraction_mode?: ExtractionMode | null;
  design_changes: MainlineRow[]; failure_modes: MainlineRow[]; countermeasures: MainlineRow[];
  artifacts: MainlineArtifact[];
  readiness: { readiness: string; allowed_export_modes: string[]; blockers: {type: string; artifact_id: string}[] };
  review_events: { id: string; artifact_id: string; from_status: string; to_status: string;
    reviewer_note?: string; created_by?: string; created_at: string;
    changed_fields?: { action?: string; proposal_id?: string } }[];
}
export interface CandidateDocument {
  id: string; title: string; updated_at: string; current_version_id: number;
  processing_status: string; text_length: number | null;
  analysis_eligible?: boolean; analysis_refusal?: string | null;
}
export interface MainlineCommand {
  request_id: string; expected_version: string; action: string; kind?: RowKind; row_id?: string;
  row?: MainlineRowInput; inputs?: MainlineInputs; review_status?: string; confirm_recheck?: boolean; confirm_scores?: boolean;
  reanalysis?: 'reanalyze' | 'keep';
  instruction?: string; reason?: string; proposal_id?: string; selected_fields?: string[];
  rows?: MainlineRowInput[]; execution_status?: string; verification_status?: string;
  change_resolutions?: ChangeResolution[];
  owner?: string; due_date?: string | null;
}
export const emptyInputs: MainlineInputs = { pattern: 'manual', extraction_mode: null, old_text: '', new_text: '',
  report_text: '', selected_document_ids: [], material: '', dimensions: '',
  operating_conditions: '', constraints: '', evidence: [] };
export const mainlineApi = {
  status: () => api.get<{ enabled: boolean }>('/drbfm/mainline/status'),
  state: (sid: string) => api.get<MainlineState>(`/drbfm/sessions/${sid}/mainline`),
  start: (request_id: string, item_name: string, inputs: MainlineInputs) =>
    api.post<MainlineState>('/drbfm/mainline/sessions', { request_id, item_name, inputs }),
  command: (sid: string, command: MainlineCommand) =>
    api.post<{ workflow: MainlineState; result: { proposal_id?: string; row_id?: string; initialized?: boolean } }>(
      `/drbfm/sessions/${sid}/mainline/commands`, command, { timeout: 120000 }),
  document: (id: string) => api.get<{ document_id: string; version_id: string; title: string; file_name: string; content: string }>(
    `/drbfm/mainline/documents/${id}`),
  documents: (q: string, offset = 0) => api.get<{ documents: CandidateDocument[]; total: number }>(
    '/drbfm/mainline/documents', { params: { q, offset } }),
};
